import { useEffect, useState, useCallback } from 'react';
import { booksAPI } from '../services/api';
import { Table, Badge, Btn, Modal, Card, Pagination, useToast, Input, Select, Spinner } from '../components/UI';

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Books() {
  const { show, ToastEl } = useToast();
  const [books, setBooks]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);
  const [meta, setMeta]           = useState({});
  const [statusFilter, setStatus] = useState('');
  const [search, setSearch]       = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editBook, setEditBook]   = useState(null);
  const [viewBook, setViewBook]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await booksAPI.list({
        page, limit: 15, status: statusFilter || undefined, search: search || undefined,
      });
      setBooks(data.data);
      setMeta(data.pagination);
    } catch { show('Failed to load books', 'error'); }
    finally { setLoading(false); }
  }, [page, statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  async function deleteBook(id, title) {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await booksAPI.delete(id);
      show('Book deleted');
      load();
    } catch (e) { show(e.response?.data?.message || 'Failed to delete', 'error'); }
  }

  const columns = [
    { key: 'title', label: 'Title', render: (v, row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {row.cover_path ? (
          <img src={booksAPI.coverUrl(row.id)} alt="" style={{ width: 38, height: 52, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}/>
        ) : (
          <div style={{ width: 38, height: 52, borderRadius: 4, background: 'var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📖</div>
        )}
        <div>
          <div style={{ fontWeight: 500 }}>{v}</div>
          {row.description && (
            <div style={{ fontSize: 11, color: 'var(--text3)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.description}
            </div>
          )}
        </div>
      </div>
    )},
    { key: 'published_date', label: 'Published', render: v => v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—' },
    { key: 'price',          label: 'Price',     render: v => v != null ? `₹${parseFloat(v).toFixed(2)}` : '—' },
    { key: 'status',         label: 'Status',    render: v => <Badge label={v} type={v}/> },
    { key: 'created_by',     label: 'By',        render: v => <span style={{ fontSize: 12 }}>{v}</span> },
    { key: 'actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 6 }}>
        <Btn size="sm" variant="outline" onClick={() => setViewBook(row)}>View</Btn>
        <Btn size="sm" variant="ghost"   onClick={() => setEditBook(row)}>Edit</Btn>
        <Btn size="sm" variant="danger"  onClick={() => deleteBook(row.id, row.title)}>Delete</Btn>
      </div>
    )},
  ];

  return (
    <div style={{ padding: 28 }}>
      {ToastEl}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Books</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text3)', fontSize: 13 }}>
            Manage the book library
          </p>
        </div>
        <Btn onClick={() => setShowCreate(true)}>+ Add Book</Btn>
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Input
            placeholder="Search title or description…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ flex: 1, minWidth: 200 }}
          />
          <Select value={statusFilter} onChange={e => { setStatus(e.target.value); setPage(1); }} style={{ width: 150 }}>
            <option value="">All statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><Spinner/></div>
        ) : (
          <Table columns={columns} data={books} emptyMsg="No books found"/>
        )}
        {meta.pages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
            <Pagination page={page} pages={meta.pages} onChange={setPage}/>
          </div>
        )}
      </Card>

      {/* Create Modal */}
      {showCreate && (
        <BookFormModal
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); show('Book created'); }}
          onError={msg => show(msg, 'error')}
        />
      )}

      {/* Edit Modal */}
      {editBook && (
        <BookFormModal
          book={editBook}
          onClose={() => setEditBook(null)}
          onSaved={() => { setEditBook(null); load(); show('Book updated'); }}
          onError={msg => show(msg, 'error')}
        />
      )}

      {/* View Modal */}
      {viewBook && (
        <ViewModal book={viewBook} onClose={() => setViewBook(null)}/>
      )}
    </div>
  );
}

// ── Book Form Modal ───────────────────────────────────────────────────────────
function BookFormModal({ book, onClose, onSaved, onError }) {
  const isEdit = !!book;
  const [saving, setSaving] = useState(false);
  const [title, setTitle]   = useState(book?.title || '');
  const [description, setDescription] = useState(book?.description || '');
  const [publishedDate, setPublishedDate] = useState(book?.published_date?.slice(0, 10) || '');
  const [status, setStatus] = useState(book?.status || 'published');
  const [price, setPrice]       = useState(book?.price != null ? String(book.price) : '');
  const [sortOrder, setSortOrder] = useState(book?.sort_order ?? 0);
  const [coverFile, setCoverFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(book?.cover_path ? booksAPI.coverUrl(book.id) : null);

  function handleCoverChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  }

  async function submit(e) {
    e.preventDefault();
    if (!title.trim()) { onError('Title is required'); return; }
    setSaving(true);
    try {
      const form = new FormData();
      form.append('title', title.trim());
      form.append('description', description.trim());
      form.append('status', status);
      if (publishedDate) form.append('published_date', publishedDate);
      if (price.trim())  form.append('price', price.trim());
      form.append('sort_order', sortOrder);
      if (coverFile) form.append('cover', coverFile);

      if (isEdit) {
        await booksAPI.update(book.id, form);
      } else {
        await booksAPI.create(form);
      }
      onSaved();
    } catch (err) {
      onError(err.response?.data?.message || 'Failed to save book');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={isEdit ? 'Edit Book' : 'Add Book'} onClose={onClose} width={520}>
      <form onSubmit={submit}>
        {/* Cover preview + upload */}
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{
            width: 80, height: 110, borderRadius: 6, background: 'var(--border)',
            flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {previewUrl ? (
              <img src={previewUrl} alt="Cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
            ) : (
              <span style={{ fontSize: 28 }}>📖</span>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 6, color: 'var(--text2)' }}>
              Cover Image
            </label>
            <input type="file" accept="image/*" onChange={handleCoverChange} style={{ fontSize: 12 }}/>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>JPG, PNG, WebP recommended</div>
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 5, color: 'var(--text2)' }}>Title *</label>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Book title" style={{ width: '100%' }}/>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 5, color: 'var(--text2)' }}>Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
            placeholder="Short description…"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '8px 10px',
              borderRadius: 7, border: '1px solid var(--border)',
              fontSize: 13, resize: 'vertical', fontFamily: 'inherit',
              background: 'var(--card)', color: 'var(--text)',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 5, color: 'var(--text2)' }}>Published Date</label>
            <Input type="date" value={publishedDate} onChange={e => setPublishedDate(e.target.value)} style={{ width: '100%' }}/>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 5, color: 'var(--text2)' }}>Price (₹)</label>
            <Input type="number" min="0" step="0.01" placeholder="0.00" value={price} onChange={e => setPrice(e.target.value)} style={{ width: '100%' }}/>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 5, color: 'var(--text2)' }}>Status</label>
            <Select value={status} onChange={e => setStatus(e.target.value)} style={{ width: '100%' }}>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </Select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 5, color: 'var(--text2)' }}>Sort Order</label>
            <Input type="number" min="0" value={sortOrder} onChange={e => setSortOrder(Number(e.target.value))} style={{ width: '100%' }}/>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <Btn variant="outline" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>
            {saving ? <Spinner size={14}/> : isEdit ? 'Save Changes' : 'Create Book'}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── View Modal ────────────────────────────────────────────────────────────────
function ViewModal({ book, onClose }) {
  return (
    <Modal title="Book Details" onClose={onClose} width={480}>
      <div style={{ display: 'flex', gap: 18, marginBottom: 20 }}>
        <div style={{
          width: 90, height: 124, borderRadius: 6, background: 'var(--border)',
          flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {book.cover_path ? (
            <img src={booksAPI.coverUrl(book.id)} alt="Cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
          ) : (
            <span style={{ fontSize: 32 }}>📖</span>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700 }}>{book.title}</h2>
          <Badge label={book.status} type={book.status}/>
          {book.published_date && (
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>
              Published: {new Date(book.published_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
            </div>
          )}
          {book.price != null && (
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
              Price: ₹{parseFloat(book.price).toFixed(2)}
            </div>
          )}
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
            Added by: {book.created_by}
          </div>
        </div>
      </div>

      {book.description && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)', marginBottom: 4 }}>Description</div>
          <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6 }}>{book.description}</div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <Btn variant="outline" onClick={onClose}>Close</Btn>
      </div>
    </Modal>
  );
}
